import api from '../../utils/api';

export type BookingOfferServiceType = 'AMBULANCE' | 'DOCTOR_CONSULTATION';

export interface BookingOfferTier {
  bookingNumber: number;
  discountPercent: number;
  title?: string;
  description?: string;
  ctaDeepLinkRouteId?: string | null;
  popupImageUrl?: string | null;
  splashImageUrl?: string | null;
}

export type BookingOfferPopupDisplayMode = 'IMAGE_ONLY' | 'TEXT_ONLY' | 'BOTH';

export interface BookingOfferConfig {
  offerConfigId: string;
  serviceType: BookingOfferServiceType;
  isActive: boolean;
  tiers: BookingOfferTier[];
  marketingTitle?: string | null;
  popupDescription?: string | null;
  popupImageUrl?: string | null;
  popupDisplayMode?: BookingOfferPopupDisplayMode;
  ctaDeepLinkRouteId?: string | null;
  showOnSplash?: boolean;
  splashDisplayMode?: BookingOfferPopupDisplayMode;
  splashImageUrl?: string | null;
  splashBackgroundColor?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface OfferRedemptionRow {
  redemptionId: string;
  userId: string;
  serviceType: BookingOfferServiceType;
  bookingNumber: number;
  discountPercent: number;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  serviceRefId?: string | null;
  paymentId?: string | null;
  status: 'RESERVED' | 'APPLIED' | 'CANCELLED';
  createdAt?: string;
}

export const BOOKING_OFFER_SERVICE_OPTIONS: {
  value: BookingOfferServiceType;
  label: string;
}[] = [
  { value: 'AMBULANCE', label: 'Ambulance' },
  { value: 'DOCTOR_CONSULTATION', label: 'Doctor Consultation' },
];

export async function fetchBookingOfferConfigs() {
  const res = await api.get('/master-admin/booking-offers');
  return (res.data?.data || []) as BookingOfferConfig[];
}

export async function fetchBookingOfferConfig(serviceType: BookingOfferServiceType) {
  const res = await api.get(`/master-admin/booking-offers/${serviceType}`);
  return res.data?.data as BookingOfferConfig;
}

export async function upsertBookingOfferConfig(
  serviceType: BookingOfferServiceType,
  payload: {
    isActive: boolean;
    tiers: BookingOfferTier[];
    marketingTitle?: string | null;
    popupDescription?: string | null;
    popupImageUrl?: string | null;
    popupDisplayMode?: BookingOfferPopupDisplayMode;
    ctaDeepLinkRouteId?: string | null;
    showOnSplash?: boolean;
    splashDisplayMode?: BookingOfferPopupDisplayMode;
    splashImageUrl?: string | null;
    splashBackgroundColor?: string | null;
    validFrom?: string | null;
    validUntil?: string | null;
  }
) {
  const res = await api.put(`/master-admin/booking-offers/${serviceType}`, payload);
  return res.data?.data as BookingOfferConfig;
}

export async function deleteBookingOfferConfig(serviceType: BookingOfferServiceType) {
  await api.delete(`/master-admin/booking-offers/${serviceType}`);
}

export async function toggleBookingOfferActive(
  serviceType: BookingOfferServiceType,
  isActive: boolean
) {
  const res = await api.patch(`/master-admin/booking-offers/${serviceType}/active`, {
    isActive,
  });
  return res.data?.data as BookingOfferConfig;
}

export async function uploadBookingOfferPopupImage(file: File) {
  const fd = new FormData();
  fd.append('image', file);
  const res = await api.post('/master-admin/booking-offers/popup-image', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data?.data?.url as string;
}

export async function fetchOfferRedemptions(params?: {
  page?: number;
  limit?: number;
  serviceType?: BookingOfferServiceType;
  userId?: string;
}) {
  const res = await api.get('/master-admin/booking-offers/redemptions', { params });
  return {
    rows: (res.data?.rows || []) as OfferRedemptionRow[],
    count: Number(res.data?.count || 0),
    page: Number(res.data?.page || 1),
    limit: Number(res.data?.limit || 20),
  };
}

export function serviceTypeLabel(serviceType: BookingOfferServiceType) {
  return (
    BOOKING_OFFER_SERVICE_OPTIONS.find((o) => o.value === serviceType)?.label ||
    serviceType
  );
}

export function tierSummary(tiers: BookingOfferTier[] = []) {
  if (!tiers.length) return '—';
  return tiers
    .map((t) => `#${t.bookingNumber}: ${t.discountPercent}%`)
    .join(' · ');
}
